// Snapshot interpolation for remote entities: buffer timestamped poses as
// they arrive, sample the pose as of (now - INTERP_DELAY_MS). Engine- and
// channel-agnostic; every renderer consumes this the same way.

import { wrapAngle } from "./movement";

export interface PoseSnapshot {
  /** Timestamp in ms (caller's clock; must be monotonic per buffer). */
  t: number;
  x: number;
  z: number;
  yaw: number;
  /** Altitude, for entities that fly (ships). Absent reads as 0. */
  y?: number;
}

function lerp(a: number, b: number, f: number): number {
  return a + (b - a) * f;
}

/** Interpolate between two angles along the shortest arc. */
export function lerpAngle(a: number, b: number, f: number): number {
  return wrapAngle(a + wrapAngle(b - a) * f);
}

export class SnapshotBuffer {
  private snaps: PoseSnapshot[] = [];

  constructor(private readonly maxAgeMs = 1000) {}

  get size(): number {
    return this.snaps.length;
  }

  push(snap: PoseSnapshot): void {
    // Snapshots normally arrive in order; tolerate the odd straggler.
    let i = this.snaps.length;
    while (i > 0 && (this.snaps[i - 1] as PoseSnapshot).t > snap.t) i--;
    this.snaps.splice(i, 0, snap);

    const newest = (this.snaps[this.snaps.length - 1] as PoseSnapshot).t;
    const cutoff = newest - this.maxAgeMs;
    let firstKept = 0;
    // Keep one snapshot older than the cutoff so sampling near it still brackets.
    while (
      firstKept < this.snaps.length - 1 &&
      (this.snaps[firstKept + 1] as PoseSnapshot).t <= cutoff
    ) {
      firstKept++;
    }
    if (firstKept > 0) this.snaps.splice(0, firstKept);
  }

  /**
   * Pose as of time t: clamped to the ends of the buffer, linearly
   * interpolated between the two bracketing snapshots otherwise.
   * Returns undefined only when the buffer is empty.
   */
  sample(t: number): PoseSnapshot | undefined {
    const snaps = this.snaps;
    if (snaps.length === 0) return undefined;

    const first = snaps[0] as PoseSnapshot;
    if (t <= first.t) return first;
    const last = snaps[snaps.length - 1] as PoseSnapshot;
    if (t >= last.t) return last;

    let hi = 1;
    while ((snaps[hi] as PoseSnapshot).t < t) hi++;
    const a = snaps[hi - 1] as PoseSnapshot;
    const b = snaps[hi] as PoseSnapshot;
    const span = b.t - a.t;
    const f = span > 0 ? (t - a.t) / span : 1;
    return {
      t,
      x: lerp(a.x, b.x, f),
      z: lerp(a.z, b.z, f),
      yaw: lerpAngle(a.yaw, b.yaw, f),
      y: lerp(a.y ?? 0, b.y ?? 0, f),
    };
  }

  clear(): void {
    this.snaps = [];
  }
}
